import React, { useMemo } from 'react';

/** Catmull-Rom interpolate between points for rounder joins. */
function smoothTrailPoints(points, segmentsPerSpan = 6) {
    if (!Array.isArray(points) || points.length < 2) return points || [];
    if (points.length === 2) return points.map((p) => ({ ...p }));

    const pts = points.map((p) => ({ x: Number(p.x), y: Number(p.y) }));
    const out = [];

    for (let i = 0; i < pts.length - 1; i += 1) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        for (let s = 0; s < segmentsPerSpan; s += 1) {
            const t = s / segmentsPerSpan;
            const t2 = t * t;
            const t3 = t2 * t;
            const x =
                0.5 *
                (2 * p1.x +
                    (-p0.x + p2.x) * t +
                    (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
                    (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3);
            const y =
                0.5 *
                (2 * p1.y +
                    (-p0.y + p2.y) * t +
                    (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
                    (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3);
            out.push({ x, y });
        }
    }
    out.push({ ...pts[pts.length - 1] });
    return out;
}

/**
 * Build a filled ribbon that tapers along the trail (wide at start → thin at end).
 * Uses averaged normals for softer corners.
 * Optional lowerBoost: widen incrementally between goal ~4 and trailhead (~7).
 */
function buildTaperedRibbon(
    points,
    startWidth,
    endWidth,
    { lowerStartY = 68, lowerEndY = 94, lowerWiden = 0.38 } = {}
) {
    if (!Array.isArray(points) || points.length < 2) return '';

    const pts = points.map((p) => ({ x: Number(p.x), y: Number(p.y) }));
    const left = [];
    const right = [];
    const last = pts.length - 1;

    // Precompute segment normals
    const normals = [];
    for (let i = 0; i < last; i += 1) {
        let tx = pts[i + 1].x - pts[i].x;
        let ty = pts[i + 1].y - pts[i].y;
        const len = Math.hypot(tx, ty) || 1;
        tx /= len;
        ty /= len;
        normals.push({ x: -ty, y: tx });
    }

    for (let i = 0; i < pts.length; i += 1) {
        let nx;
        let ny;
        if (i === 0) {
            nx = normals[0].x;
            ny = normals[0].y;
        } else if (i === last) {
            nx = normals[last - 1].x;
            ny = normals[last - 1].y;
        } else {
            // average adjacent normals and renormalize (miter-lite, softer joins)
            nx = normals[i - 1].x + normals[i].x;
            ny = normals[i - 1].y + normals[i].y;
            const nLen = Math.hypot(nx, ny) || 1;
            nx /= nLen;
            ny /= nLen;
        }

        const t = last === 0 ? 0 : i / last;
        const ease = t * t; // more taper near summit
        let half = (startWidth + (endWidth - startWidth) * ease) / 2;

        // Goals 4 → 7: path widens a little more toward the trailhead
        if (pts[i].y >= lowerStartY && lowerEndY > lowerStartY) {
            const u = Math.max(
                0,
                Math.min(1, (pts[i].y - lowerStartY) / (lowerEndY - lowerStartY))
            );
            // Ease-in so growth is gentle at 4 and stronger near 7
            const grow = u * u * 0.35 + u * 0.65;
            half *= 1 + lowerWiden * grow;
        }

        left.push({
            x: pts[i].x + nx * half,
            y: pts[i].y + ny * half,
        });
        right.push({
            x: pts[i].x - nx * half,
            y: pts[i].y - ny * half,
        });
    }

    const parts = [];
    parts.push(`M ${left[0].x.toFixed(2)} ${left[0].y.toFixed(2)}`);
    for (let i = 1; i < left.length; i += 1) {
        parts.push(`L ${left[i].x.toFixed(2)} ${left[i].y.toFixed(2)}`);
    }
    for (let i = right.length - 1; i >= 0; i -= 1) {
        parts.push(`L ${right[i].x.toFixed(2)} ${right[i].y.toFixed(2)}`);
    }
    parts.push('Z');
    return parts.join(' ');
}

function trailPointsFromProps(pathSamples, stops) {
    if (Array.isArray(pathSamples) && pathSamples.length >= 2) {
        return pathSamples.map((p) => ({ x: Number(p.x), y: Number(p.y) }));
    }
    if (Array.isArray(stops) && stops.length >= 2) {
        return [...stops]
            .slice()
            .sort((a, b) => (b.rank || 0) - (a.rank || 0))
            .map((p) => ({ x: Number(p.x), y: Number(p.y) }));
    }
    return null;
}

/**
 * Decorative gold journey trail.
 * Tapered ribbon (wide near trailhead, thin near summit) for depth.
 */
export default function GoalsJourneyPath({
    pathD,
    pathSamples = null,
    stops = [],
    debug = false,
    liveStroke = null,
}) {
    const liveD =
        Array.isArray(liveStroke) && liveStroke.length >= 2
            ? liveStroke
                  .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
                  .join(' ')
            : '';

    const trailPts = useMemo(
        () => trailPointsFromProps(pathSamples, stops),
        [pathSamples, stops]
    );

    const ribbons = useMemo(() => {
        if (!trailPts) return null;
        const smooth = smoothTrailPoints(trailPts, 8);
        const lower = { lowerStartY: 68, lowerEndY: 94, lowerWiden: 0.4 };
        return {
            // Soft diffuse gold bloom + taper; lower trail (goals 4→7) widens a bit
            bloom: buildTaperedRibbon(smooth, 4.6, 1.05, lower),
            haze: buildTaperedRibbon(smooth, 3.35, 0.7, lower),
            mid: buildTaperedRibbon(smooth, 1.95, 0.4, lower),
            body: buildTaperedRibbon(smooth, 1.2, 0.26, lower),
            core: buildTaperedRibbon(smooth, 0.62, 0.15, lower),
        };
    }, [trailPts]);

    if (!pathD && !liveD && !ribbons) return null;

    return (
        <svg
            className="dv2-gjourney__rail"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
        >
            <defs>
                <linearGradient
                    id="dv2-gj-depth-fade"
                    x1="0%"
                    y1="100%"
                    x2="0%"
                    y2="0%"
                >
                    <stop offset="0%" stopColor="rgba(255, 214, 100, 0.24)" />
                    <stop offset="50%" stopColor="rgba(255, 222, 140, 0.17)" />
                    <stop offset="100%" stopColor="rgba(255, 236, 170, 0.11)" />
                </linearGradient>
                <linearGradient
                    id="dv2-gj-glow-fade"
                    x1="0%"
                    y1="100%"
                    x2="0%"
                    y2="0%"
                >
                    {/* Trailhead: quieter / less bloom */}
                    <stop offset="0%" stopColor="rgba(255, 196, 70, 0.085)" />
                    <stop offset="35%" stopColor="rgba(255, 210, 100, 0.14)" />
                    <stop offset="70%" stopColor="rgba(255, 220, 120, 0.24)" />
                    {/* Summit: soft premium gold, not neon */}
                    <stop offset="100%" stopColor="rgba(255, 230, 150, 0.32)" />
                </linearGradient>
                <linearGradient
                    id="dv2-gj-core-fade"
                    x1="0%"
                    y1="100%"
                    x2="0%"
                    y2="0%"
                >
                    <stop offset="0%" stopColor="rgba(255, 232, 160, 0.5)" />
                    <stop offset="45%" stopColor="rgba(255, 236, 175, 0.4)" />
                    <stop offset="80%" stopColor="rgba(255, 240, 190, 0.3)" />
                    <stop offset="100%" stopColor="rgba(255, 244, 210, 0.19)" />
                </linearGradient>
                <filter
                    id="dv2-gj-path-glow-bloom"
                    x="-180%"
                    y="-180%"
                    width="460%"
                    height="460%"
                >
                    <feGaussianBlur stdDeviation="5.8" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                    </feMerge>
                </filter>
                <filter
                    id="dv2-gj-path-glow-soft"
                    x="-160%"
                    y="-160%"
                    width="420%"
                    height="420%"
                >
                    <feGaussianBlur stdDeviation="4.2" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                    </feMerge>
                </filter>
                <filter
                    id="dv2-gj-path-glow-mid"
                    x="-110%"
                    y="-110%"
                    width="320%"
                    height="320%"
                >
                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                    </feMerge>
                </filter>
                <filter
                    id="dv2-gj-path-glow"
                    x="-80%"
                    y="-80%"
                    width="260%"
                    height="260%"
                >
                    <feGaussianBlur stdDeviation="1.2" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                    </feMerge>
                </filter>
                <filter
                    id="dv2-gj-core-soft"
                    x="-80%"
                    y="-80%"
                    width="260%"
                    height="260%"
                >
                    <feGaussianBlur stdDeviation="0.6" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
                <filter
                    id="dv2-gj-stop-glow"
                    x="-120%"
                    y="-120%"
                    width="340%"
                    height="340%"
                >
                    <feGaussianBlur stdDeviation="0.45" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            {ribbons ? (
                <>
                    {/* Diffuse gold glow — quieter at base, stronger toward summit */}
                    <path
                        d={ribbons.bloom}
                        fill="url(#dv2-gj-glow-fade)"
                        stroke="none"
                        filter="url(#dv2-gj-path-glow-bloom)"
                        opacity="0.24"
                    />
                    <path
                        d={ribbons.haze}
                        fill="url(#dv2-gj-glow-fade)"
                        stroke="none"
                        filter="url(#dv2-gj-path-glow-soft)"
                        opacity="0.29"
                    />
                    <path
                        d={ribbons.mid}
                        fill="url(#dv2-gj-glow-fade)"
                        stroke="none"
                        filter="url(#dv2-gj-path-glow-mid)"
                        opacity="0.27"
                    />
                    <path
                        d={ribbons.body}
                        fill="url(#dv2-gj-depth-fade)"
                        stroke="none"
                        filter="url(#dv2-gj-path-glow)"
                        opacity="0.66"
                    />
                    {/* Central line */}
                    <path
                        d={ribbons.core}
                        fill="url(#dv2-gj-core-fade)"
                        stroke="none"
                        filter="url(#dv2-gj-core-soft)"
                    />
                </>
            ) : pathD ? (
                <>
                    <path
                        d={pathD}
                        fill="none"
                        stroke="rgba(255, 210, 100, 0.2)"
                        strokeWidth="2.4"
                        vectorEffect="non-scaling-stroke"
                        strokeLinecap="round"
                        filter="url(#dv2-gj-path-glow-mid)"
                    />
                    <path
                        d={pathD}
                        fill="none"
                        stroke="rgba(255, 236, 170, 0.42)"
                        strokeWidth="1"
                        vectorEffect="non-scaling-stroke"
                        strokeLinecap="round"
                    />
                </>
            ) : null}

            {liveD ? (
                <>
                    <path
                        d={liveD}
                        fill="none"
                        stroke="rgba(0, 255, 200, 0.35)"
                        strokeWidth="8"
                        vectorEffect="non-scaling-stroke"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d={liveD}
                        fill="none"
                        stroke="rgba(0, 255, 200, 0.95)"
                        strokeWidth="3"
                        vectorEffect="non-scaling-stroke"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </>
            ) : null}

            {stops.map((stop) => (
                <circle
                    key={`stop-${stop.rank || `${stop.x}-${stop.y}`}`}
                    cx={stop.x}
                    cy={stop.y}
                    r={stop.type === 'oolaOne' ? 0.85 : 0.45}
                    fill={
                        stop.type === 'oolaOne'
                            ? 'rgba(255, 214, 100, 0.34)'
                            : 'rgba(255, 214, 100, 0.18)'
                    }
                    filter="url(#dv2-gj-stop-glow)"
                />
            ))}

            {debug
                ? stops.map((stop) => (
                      <g
                          key={`dbg-${stop.rank || `${stop.x}-${stop.y}`}`}
                          className="dv2-gjourney__debug"
                      >
                          <line
                              x1={stop.x - 2.2}
                              y1={stop.y}
                              x2={stop.x + 2.2}
                              y2={stop.y}
                              stroke="rgba(0, 255, 200, 0.9)"
                              strokeWidth="0.35"
                              vectorEffect="non-scaling-stroke"
                          />
                          <line
                              x1={stop.x}
                              y1={stop.y - 2.2}
                              x2={stop.x}
                              y2={stop.y + 2.2}
                              stroke="rgba(0, 255, 200, 0.9)"
                              strokeWidth="0.35"
                              vectorEffect="non-scaling-stroke"
                          />
                          <text
                              x={stop.x + 2.6}
                              y={stop.y - 1.4}
                              fill="rgba(0, 255, 200, 0.95)"
                              fontSize="2.4"
                              style={{ fontFamily: 'monospace' }}
                          >
                              {`#${stop.rank} ${stop.x},${stop.y}`}
                          </text>
                      </g>
                  ))
                : null}
        </svg>
    );
}
